import {
  Clear as ClearIcon,
  GetApp as ExportIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  alpha,
  Box,
  Checkbox,
  Chip,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

// Types
export interface DataTableColumn<T = any> {
  /** Уникальный идентификатор колонки */
  id: keyof T | string;
  /** Заголовок колонки */
  label: string;
  /** Минимальная ширина */
  minWidth?: number;
  /** Выравнивание содержимого */
  align?: "left" | "center" | "right";
  /** Можно ли сортировать */
  sortable?: boolean;
  /** Можно ли фильтровать */
  filterable?: boolean;
  /** Кастомная функция рендеринга */
  render?: (value: any, row: T, index: number) => React.ReactNode;
  /** Функция форматирования для экспорта */
  exportFormat?: (value: any) => string;
  /** Скрыть колонку */
  hidden?: boolean;
  /** Sticky колонка */
  sticky?: "left" | "right";
  /** Тип данных для фильтрации */
  dataType?: "string" | "number" | "date" | "boolean";
}

export interface DataTableFilter {
  column: string;
  operator:
    | "contains"
    | "equals"
    | "startsWith"
    | "endsWith"
    | "gt"
    | "lt"
    | "gte"
    | "lte";
  value: any;
}

export interface DataTableSort {
  column: string;
  direction: "asc" | "desc";
}

export interface DataTableProps<T = any> {
  /** Данные для отображения */
  data: T[];
  /** Конфигурация колонок */
  columns: DataTableColumn<T>[];
  /** Уникальный ключ для каждой строки */
  rowKey: keyof T | ((row: T) => string | number);
  /** Заголовок таблицы */
  title?: string;
  /** Загрузка данных */
  loading?: boolean;
  /** Высота таблицы */
  height?: number | string;
  /** Включить пагинацию */
  pagination?: boolean;
  /** Размер страницы по умолчанию */
  defaultPageSize?: number;
  /** Доступные размеры страниц */
  pageSizeOptions?: number[];
  /** Включить множественный выбор */
  selectable?: boolean;
  /** Выбранные строки */
  selectedRows?: (string | number)[];
  /** Callback при изменении выбора */
  onSelectionChange?: (selectedRows: (string | number)[]) => void;
  /** Включить поиск */
  searchable?: boolean;
  /** Placeholder для поиска */
  searchPlaceholder?: string;
  /** Локализация */
  localeText?: {
    searchPlaceholder?: string;
    emptyText?: string;
    selectedLabel?: string;
    exportTitle?: string;
    filterTitle?: string;
    rowsPerPageLabel?: string;
    displayedRowsLabel?: (from: number, to: number, count: number) => string;
  };
  /** Включить фильтрацию */
  filterable?: boolean;
  /** Включить экспорт */
  exportable?: boolean;
  /** Имя файла для экспорта */
  exportFileName?: string;
  /** Callback при клике на строку */
  onRowClick?: (row: T, index: number) => void;
  /** Callback при двойном клике на строку */
  onRowDoubleClick?: (row: T, index: number) => void;
  /** Пустое состояние */
  emptyState?: React.ReactNode;
  /** Кастомные действия в toolbar */
  actions?: React.ReactNode;
  /** Плотность таблицы */
  density?: "comfortable" | "standard" | "compact";
  /** Включить виртуализацию для больших данных */
  virtualized?: boolean;
  /** Высота строки для виртуализации */
  rowHeight?: number;
  /** Sticky заголовок */
  stickyHeader?: boolean;
  /** Пользовательский класс */
  className?: string;
}

type MuiTableSize = "small" | "medium";

// Styled Components
const StyledTableContainer = styled(TableContainer)<{
  height?: number | string;
}>(({ theme, height }) => ({
  height: height || "auto",
  maxHeight: height || "70vh",
  "& .MuiTableCell-stickyHeader": {
    backgroundColor: theme.palette.background.paper,
    zIndex: 2,
  },
}));

const StyledTableCell = styled(TableCell)<{ sticky?: "left" | "right" }>(
  ({ theme, sticky }) => ({
    ...(sticky && {
      position: "sticky",
      zIndex: 1,
      backgroundColor: theme.palette.background.paper,
      [sticky]: 0,
      boxShadow:
        sticky === "left"
          ? `2px 0 4px ${alpha(theme.palette.common.black, 0.1)}`
          : `-2px 0 4px ${alpha(theme.palette.common.black, 0.1)}`,
    }),
  })
);

const FilterChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  "& .MuiChip-deleteIcon": {
    fontSize: "16px",
  },
}));

/**
 * Enterprise-level DataTable компонент
 *
 * @example
 * ```tsx
 * <DataTable
 *   data={users}
 *   columns={userColumns}
 *   rowKey="id"
 *   title="Users"
 *   searchable
 *   filterable
 *   selectable
 *   exportable
 *   pagination
 * />
 * ```
 */
export const DataTable = <T extends Record<string, any>>({
  data,
  columns,
  rowKey,
  title,
  loading = false,
  height,
  pagination = true,
  defaultPageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  selectable = false,
  selectedRows,
  onSelectionChange,
  searchable = false,
  searchPlaceholder,
  localeText,
  filterable = false,
  exportable = false,
  exportFileName = "export",
  onRowClick,
  onRowDoubleClick,
  emptyState,
  actions,
  density = "standard",
  virtualized: _virtualized = false,
  rowHeight: _rowHeight = 53,
  stickyHeader = true,
  className,
}: DataTableProps<T>) => {
  const { t } = useTranslation('common');
  const resolvedLocale = {
    searchPlaceholder: localeText?.searchPlaceholder ?? searchPlaceholder ?? t('table.searchPlaceholder'),
    emptyText: localeText?.emptyText ?? t('table.noData'),
    selectedLabel: localeText?.selectedLabel ?? t('table.selected'),
    exportTitle: localeText?.exportTitle ?? t('table.exportCsv'),
    filterTitle: localeText?.filterTitle ?? t('table.filter'),
    rowsPerPageLabel: localeText?.rowsPerPageLabel ?? t('table.rowsPerPage'),
    displayedRowsLabel:
      localeText?.displayedRowsLabel ??
      ((from: number, to: number, count: number) =>
        t('table.displayedRows', { from, to, count })),
  };
  // State
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<DataTableSort | null>(null);
  const [filters, setFilters] = useState<DataTableFilter[]>([]);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [_selectedColumn, setSelectedColumn] = useState<string>("");
  const [internalSelection, setInternalSelection] =
    useState<(string | number)[]>(selectedRows || []);

  useTheme();
  const tableRef = useRef<HTMLDivElement>(null);

  // Helper functions
  const getRowKey = useCallback(
    (row: T, index: number): string | number => {
      return typeof rowKey === "function"
        ? rowKey(row)
        : (row[rowKey] ?? index);
    },
    [rowKey]
  );

  // Filter and sort data
  const processedData = useMemo(() => {
    let result = [...data];

    // Search
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter((row) =>
        columns.some((col) => {
          if (col.hidden) return false;
          const value = row[col.id];
          return value?.toString().toLowerCase().includes(searchLower);
        })
      );
    }

    // Filters
    if (filters.length > 0) {
      result = result.filter((row) => {
        return filters.every((filter) => {
          const value = row[filter.column];
          if (value == null) return false;

          switch (filter.operator) {
            case "contains":
              return value
                .toString()
                .toLowerCase()
                .includes(filter.value.toString().toLowerCase());
            case "equals":
              return value === filter.value;
            case "startsWith":
              return value
                .toString()
                .toLowerCase()
                .startsWith(filter.value.toString().toLowerCase());
            case "endsWith":
              return value
                .toString()
                .toLowerCase()
                .endsWith(filter.value.toString().toLowerCase());
            case "gt":
              return Number(value) > Number(filter.value);
            case "lt":
              return Number(value) < Number(filter.value);
            case "gte":
              return Number(value) >= Number(filter.value);
            case "lte":
              return Number(value) <= Number(filter.value);
            default:
              return true;
          }
        });
      });
    }

    // Sort
    if (sortConfig) {
      result.sort((a, b) => {
        const aVal = a[sortConfig.column];
        const bVal = b[sortConfig.column];

        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, searchTerm, filters, sortConfig, columns]);

  // Pagination
  const paginatedData = useMemo(() => {
    if (!pagination) return processedData;
    const start = page * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, page, pageSize, pagination]);

  // Event handlers
  const handleSort = (column: string) => {
    const col = columns.find((c) => c.id === column);
    if (!col?.sortable) return;

    setSortConfig((prev) => ({
      column,
      direction:
        prev?.column === column && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleSelectAll = (checked: boolean) => {
    const newSelection = checked
      ? processedData.map((row, index) => getRowKey(row, index))
      : [];
    setInternalSelection(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleSelectRow = (rowKey: string | number, checked: boolean) => {
    const newSelection = checked
      ? [...internalSelection, rowKey]
      : internalSelection.filter((key) => key !== rowKey);
    setInternalSelection(newSelection);
    onSelectionChange?.(newSelection);
  };

  const handleAddFilter = (
    column: string,
    operator: DataTableFilter["operator"],
    value: any
  ) => {
    const newFilter: DataTableFilter = { column, operator, value };
    setFilters((prev) => [...prev, newFilter]);
    setFilterMenuAnchor(null);
  };

  const handleRemoveFilter = (index: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExport = () => {
    const headers = columns
      .filter((col) => !col.hidden)
      .map((col) => col.label);
    const rows = processedData.map((row) =>
      columns
        .filter((col) => !col.hidden)
        .map((col) => {
          const value = row[col.id];
          return col.exportFormat
            ? col.exportFormat(value)
            : value?.toString() || "";
        })
    );

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    const blob = new Blob(['﻿' + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${exportFileName}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Sync external selection (only when provided and actually changed)
  useEffect(() => {
    if (selectedRows === undefined) return;
    setInternalSelection(prev => {
      const sameLength = prev.length === selectedRows.length;
      const sameItems = sameLength && prev.every((v, i) => v === selectedRows[i]);
      return sameItems ? prev : selectedRows;
    });
  }, [selectedRows]);

  const visibleColumns = columns.filter((col) => !col.hidden);
  const isAllSelected =
    processedData.length > 0 &&
    processedData.every((row, index) =>
      internalSelection.includes(getRowKey(row, index))
    );
  const isIndeterminate = internalSelection.length > 0 && !isAllSelected;

  const mapDensityToTableSize = (
    currentDensity: DataTableProps<T>["density"]
  ): MuiTableSize => {
    switch (currentDensity) {
      case "compact":
        return "small";
      case "standard":
        return "medium";
      case "comfortable":
        return "medium"; // Or "small" based on desired comfortable mapping
      default:
        return "medium";
    }
  };

  return (
    <Paper elevation={2} {...(className != null && className !== '' ? { className } : {})}>
      {/* Toolbar */}
      <Toolbar sx={{ pl: 2, pr: 1 }}>
        <Typography variant="h6" component="div" sx={{ flex: "1 1 100%" }}>
          {title}
          {internalSelection.length > 0 && (
            <Typography
              component="span"
              variant="subtitle1"
              color="primary"
              sx={{ ml: 1 }}
            >
              ({internalSelection.length} {resolvedLocale.selectedLabel})
            </Typography>
          )}
        </Typography>

        {/* Search */}
        {searchable && (
          <TextField
            size="small"
            placeholder={resolvedLocale.searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "action.active" }} />
              ),
              endAdornment: searchTerm && (
                <IconButton size="small" onClick={() => setSearchTerm("")}>
                  <ClearIcon />
                </IconButton>
              ),
            }}
            sx={{ mr: 1, minWidth: 200 }}
          />
        )}

        {/* Filter */}
        {filterable && (
          <IconButton
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            color={filters.length > 0 ? "primary" : "default"}
          >
            <FilterIcon />
          </IconButton>
        )}

        {/* Export */}
        {exportable && (
          <IconButton onClick={handleExport} title={resolvedLocale.exportTitle}>
            <ExportIcon />
          </IconButton>
        )}

        {/* Custom actions */}
        {actions}
      </Toolbar>

      {/* Active Filters */}
      {filters.length > 0 && (
        <Box sx={{ p: 2, pt: 0 }}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ mb: 1, display: "block" }}
          >
            Active Filters:
          </Typography>
          {filters.map((filter, index) => (
            <FilterChip
              key={index}
              size="small"
              label={`${filter.column} ${filter.operator} ${filter.value}`}
              onDelete={() => handleRemoveFilter(index)}
            />
          ))}
        </Box>
      )}

      {/* Table */}
      <StyledTableContainer ref={tableRef} {...(height != null ? { height } : {})}>
        {loading ? (
          <Table stickyHeader={stickyHeader} size={mapDensityToTableSize(density)}>
            <TableHead>
              <TableRow>
                {selectable && (
                  <StyledTableCell padding="checkbox">
                    <Checkbox disabled />
                  </StyledTableCell>
                )}
                {visibleColumns.map((column) => (
                  <StyledTableCell key={column.id as string} {...(column.align != null ? { align: column.align } : {})}>
                    {column.label}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: 6 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`}>
                  {selectable && (
                    <StyledTableCell padding="checkbox">
                      <Skeleton variant="circular" width={24} height={24} />
                    </StyledTableCell>
                  )}
                  {visibleColumns.map((column) => (
                    <StyledTableCell key={`${String(column.id)}-${index}`}>
                      <Skeleton variant="text" />
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : processedData.length === 0 ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight={200}
          >
            {emptyState || (
              <Typography variant="body2" color="textSecondary">
                {resolvedLocale.emptyText}
              </Typography>
            )}
          </Box>
        ) : (
          <Table
            stickyHeader={stickyHeader}
            size={mapDensityToTableSize(density)}
          >
            <TableHead>
              <TableRow>
                {selectable && (
                  <StyledTableCell padding="checkbox">
                    <Checkbox
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </StyledTableCell>
                )}
                {visibleColumns.map((column) => (
                  <StyledTableCell
                    key={column.id as string}
                    {...(column.align != null ? { align: column.align } : {})}
                    {...(column.minWidth != null ? { style: { minWidth: column.minWidth } } : {})}
                    {...(column.sticky != null ? { sticky: column.sticky } : {})}
                  >
                    {column.sortable ? (
                      <TableSortLabel
                        active={sortConfig?.column === column.id}
                        direction={
                          sortConfig?.column === column.id
                            ? sortConfig.direction
                            : "asc"
                        }
                        onClick={() => handleSort(column.id as string)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : (
                      column.label
                    )}
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => {
                const key = getRowKey(row, index);
                const isSelected = internalSelection.includes(key);

                return (
                  <TableRow
                    key={key}
                    hover
                    selected={isSelected}
                    onClick={() => onRowClick?.(row, index)}
                    onDoubleClick={() => onRowDoubleClick?.(row, index)}
                    sx={{ cursor: onRowClick ? "pointer" : "default" }}
                  >
                    {selectable && (
                      <StyledTableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) =>
                            handleSelectRow(key, e.target.checked)
                          }
                        />
                      </StyledTableCell>
                    )}
                    {visibleColumns.map((column) => (
                      <StyledTableCell
                        key={column.id as string}
                        {...(column.align != null ? { align: column.align } : {})}
                        {...(column.sticky != null ? { sticky: column.sticky } : {})}
                      >
                        {column.render
                          ? column.render(row[column.id], row, index)
                          : row[column.id]?.toString() || ""}
                      </StyledTableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </StyledTableContainer>

      {/* Pagination */}
      {pagination && !loading && processedData.length > 0 && (
        <TablePagination
          component="div"
          count={processedData.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={pageSizeOptions}
          labelRowsPerPage={resolvedLocale.rowsPerPageLabel}
          labelDisplayedRows={({ from, to, count }) => resolvedLocale.displayedRowsLabel(from, to, count)}
        />
      )}

      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        {visibleColumns
          .filter((col) => col.filterable)
          .map((col) => (
            <MenuItem
              key={col.id as string}
              onClick={() => {
                setSelectedColumn(col.id as string);
                handleAddFilter(col.id as string, 'contains', '');
                setFilterMenuAnchor(null);
              }}
            >
              {resolvedLocale.filterTitle}: {col.label}
            </MenuItem>
          ))}
      </Menu>
    </Paper>
  );
};
